import { handleUpdateOp, updateQueryCache, updateQueryCacheCollection } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import useAuthStore from "~/store/auth";
import { OnboardingStatus, OnboardingStepType } from "~/types/onboarding";
import { agent } from "~/util/agent";
import { useDocMetadata, useDocsMetadata, useDownloadDoc, useUploadDoc } from "./document";

const QUERY_DOCUMENTS_METADATA_KEY = "onboarding-documents-metadata";
const QUERY_ONBOARDING_SESSIONS_KEY = "onboarding-sessions";
const QUERY_ONBOARDING_REVIEW_SESSIONS_KEY = "onboarding-sessions-in-review";
const QUERY_ONBOARDING_SESSION_STEPS_KEY = "onboarding-session-steps";

const OnboardingCreateSchema = z.object({
  counterparty_id: z.string().uuid().min(1),
});

const OnboardingReviewSchema = z.object({
  comments: z.string().optional(),
  status: z.nativeEnum(OnboardingStatus),
});

const OnboardingSchema = OnboardingCreateSchema.extend({
  id: z.string().uuid().min(1),
  org_id: z.string().uuid().min(1),
  overall_status: z.nativeEnum(OnboardingStatus),
});

const OnboardingStepSchema = z.object({
  id: z.string().uuid().min(1),
  onboarding_session_id: z.string().uuid().min(1),
  onboarding_data: z.any(),
  status: z.nativeEnum(OnboardingStatus),
  step_type: z.nativeEnum(OnboardingStepType),
  comments: z.string(),
});

const OnboardingUpdateSchema = OnboardingStepSchema.extend({
  counterparty_id: z.string().uuid().min(1),
});

type OnboardingResourceType = z.infer<typeof OnboardingSchema>;
type OnboardingStepResourceType = z.infer<typeof OnboardingStepSchema>;
type UpdateOnboardingInput = z.infer<typeof OnboardingUpdateSchema>;
type ReviewOnboardingStepInput = z.infer<typeof OnboardingReviewSchema>;

const useOnboardingSessions = (counterpartyId?: string) => {
  return useQuery(
    [QUERY_ONBOARDING_SESSIONS_KEY, counterpartyId],
    async () => agent(`counterparties/${counterpartyId}/onboarding/`).json<OnboardingResourceType[]>(),
    {
      enabled: !!counterpartyId,
    }
  );
};

const useOnboardingSession = (counterpartyId?: string, id?: string) =>
  useQuery(
    [QUERY_ONBOARDING_SESSIONS_KEY, counterpartyId, id],
    async () => agent(`counterparties/${counterpartyId}/onboarding/${id}`).json<OnboardingResourceType>(),
    {
      enabled: !!id && !!counterpartyId,
    }
  );

const useOnboardingReviewSessions = () =>
  useQuery([QUERY_ONBOARDING_REVIEW_SESSIONS_KEY], async () => agent(`onboarding/`).json<OnboardingResourceType[]>());

const useOnboardingSessionSteps = (counterpartyId?: string, onboardingSessionId?: string) =>
  useQuery(
    [QUERY_ONBOARDING_SESSION_STEPS_KEY, counterpartyId, onboardingSessionId],
    async () =>
      agent(`counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step`)
        .json<OnboardingStepResourceType[]>()
        .then((r) => {
          return [
            ...r,
            {
              id: OnboardingStepType.SUBMIT,
              status: OnboardingStatus.NOT_STARTED,
              step_type: OnboardingStepType.SUBMIT,
              comments: "",
            },
          ];
        }),
    {
      enabled: !!counterpartyId && !!onboardingSessionId,
    }
  );

const useOnboardingSessionStep = (counterpartyId?: string, onboardingSessionId?: string, stepId?: string) =>
  useQuery(
    [QUERY_ONBOARDING_SESSION_STEPS_KEY, counterpartyId, onboardingSessionId, stepId],
    async () =>
      agent(
        `counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step/${stepId}`
      ).json<OnboardingStepResourceType>(),
    {
      enabled: !!counterpartyId && !!onboardingSessionId && !!stepId,
    }
  );

const useSubmitOnboardingSession = (counterpartyId?: string, onboardingSessionId?: string) => {
  return useMutation<unknown, Error, void>(
    () => {
      return agent.put(`counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/submit`, {
        labels: ["submitted", "onboarding_form"],
      });
    },
    {
      onSuccess(data, variables) {
        // Need to update both the step collection and step details stores.
        updateQueryCacheCollection(
          [QUERY_ONBOARDING_SESSIONS_KEY, counterpartyId],
          { status: OnboardingStatus.IN_REVIEW },
          variables
        );
        updateQueryCache(
          [QUERY_ONBOARDING_SESSIONS_KEY, counterpartyId, onboardingSessionId],
          { status: OnboardingStatus.IN_REVIEW },
          variables
        );
      },
    }
  );
};

const useUpdateOnboaringStep = (counterpartyId?: string, onboardingSessionId?: string, stepId?: string) => {
  return useMutation<OnboardingStepResourceType, Error, UpdateOnboardingInput>(
    (data) => {
      return agent
        .put(`counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step/${stepId}`, { json: data })
        .json<OnboardingStepResourceType>();
    },
    {
      onSuccess(data, variables) {
        // Need to update both the step collection and step details stores.
        updateQueryCacheCollection(
          [QUERY_ONBOARDING_SESSION_STEPS_KEY, counterpartyId, onboardingSessionId],
          data,
          variables
        );
        updateQueryCache(
          [QUERY_ONBOARDING_SESSION_STEPS_KEY, counterpartyId, onboardingSessionId, stepId],
          data,
          variables
        );
      },
    }
  );
};

const useReviewOnboardingStep = (counterpartyId?: string, onboardingSessionId?: string, stepId?: string) => {
  return useMutation<unknown, Error, ReviewOnboardingStepInput>(
    (data) => {
      return agent.put(`counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step/${stepId}/review`, {
        labels: ["updated", "specified_information"],
        json: data,
      });
    },
    {
      onSuccess(data, variables) {
        // Need to update both the step collection and step details stores.
        updateQueryCacheCollection(
          [QUERY_ONBOARDING_SESSION_STEPS_KEY, counterpartyId, onboardingSessionId],
          data,
          variables
        );
        updateQueryCache(
          [QUERY_ONBOARDING_SESSION_STEPS_KEY, counterpartyId, onboardingSessionId, stepId],
          data,
          variables
        );
      },
    }
  );
};

const useReviewOnboardingSession = (counterpartyId?: string, onboardingSessionId?: string) => {
  return useMutation<unknown, Error, { overall_status: OnboardingStatus }>(
    (data) => {
      return agent.put(`counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/review`, {
        labels: ["updated", "specified_information"],
        json: data,
      });
    },
    { ...handleUpdateOp([QUERY_ONBOARDING_SESSIONS_KEY, counterpartyId, onboardingSessionId]) }
  );
};

const useOnboardingDocsMetadata = (counterpartyId?: string, onboardingSessionId?: string, onboardingStepId?: string) =>
  useDocsMetadata(
    [QUERY_DOCUMENTS_METADATA_KEY, counterpartyId, onboardingSessionId, onboardingStepId],
    `counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step/${onboardingStepId}`,
    !!counterpartyId && !!onboardingSessionId && !!onboardingStepId
  );

const useOnboardingDocMetadata = (
  counterpartyId?: string,
  onboardingSessionId?: string,
  onboardingStepId?: string,
  documentId?: string
) =>
  useDocMetadata(
    [QUERY_DOCUMENTS_METADATA_KEY, counterpartyId, onboardingSessionId, onboardingStepId, documentId],
    `counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step/${onboardingStepId}`,
    documentId,
    !!counterpartyId && !!onboardingSessionId && !!onboardingStepId && !!documentId
  );

const useUploadOnboardingDoc = (counterpartyId?: string, onboardingSessionId?: string, onboardingStepId?: string) => {
  return useUploadDoc(`counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step/${onboardingStepId}`);
};

const useDownloadOnboardingDoc = (counterpartyId?: string, onboardingSessionId?: string, onboardingStepId?: string) => {
  return useDownloadDoc(`counterparties/${counterpartyId}/onboarding/${onboardingSessionId}/step/${onboardingStepId}`);
};

export {
  OnboardingReviewSchema,
  QUERY_ONBOARDING_REVIEW_SESSIONS_KEY,
  QUERY_ONBOARDING_SESSION_STEPS_KEY,
  QUERY_ONBOARDING_SESSIONS_KEY,
  useDownloadOnboardingDoc,
  useOnboardingDocMetadata,
  useOnboardingDocsMetadata,
  useOnboardingReviewSessions,
  useOnboardingSession,
  useOnboardingSessions,
  useOnboardingSessionStep,
  useOnboardingSessionSteps,
  useReviewOnboardingSession,
  useReviewOnboardingStep,
  useSubmitOnboardingSession,
  useUpdateOnboaringStep,
  useUploadOnboardingDoc,
};
export type { OnboardingResourceType, OnboardingStepResourceType, ReviewOnboardingStepInput, UpdateOnboardingInput };
